import {
	CreateDrawerFormProps,
	FormDrawer,
	FormDrawerContent,
	FormDrawerContentProps,
	FormFetchDrawer,
	TextField,
	UpdateDrawerFormProps,
} from "@dgs/core";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { contactViewService } from "../shared/api/contactViewService";
import { useContactAttributeOptions } from "../shared/hooks/useContactAttributeOptions";
import { ContactAttributeOption, ContactViewFormValues, ContactViewUpdateResource } from "../shared/types";
import { EditSortableContactAttributesField } from "./EditSortableContactAttributesField";

const useValidate = () => {
	const { t } = useTranslation();

	return (values: ContactViewFormValues) => {
		const errors: { [K in keyof ContactViewFormValues]?: any } = {};

		if (!values.name) {
			errors.name = t("Required field");
		}

		if (values.contactAttributes.length === 0) {
			errors.contactAttributes = t("Choose at least one contact attribute for this contact view.");
		}

		if (values.isDefaultContactView) {
			const contactAttributeTypes = values.contactAttributes.map(({ attribute }) => attribute.entityType);
			if (!contactAttributeTypes.includes("primaryEmail") && !contactAttributeTypes.includes("emails")) {
				errors.contactAttributes = t("The contact view must contain primary email or emails.");
			} else if (!contactAttributeTypes.includes("primaryCompany") && !contactAttributeTypes.includes("companies")) {
				errors.contactAttributes = t("The contact view must contain primary company or companies.");
			} else if (!contactAttributeTypes.includes("firstName") || !contactAttributeTypes.includes("lastName")) {
				errors.contactAttributes = t("The contact view must contain first and last name.");
			} else if (contactAttributeTypes.includes("primaryEmail") && contactAttributeTypes.includes("emails")) {
				errors.contactAttributes = t("The contact view cannot contain both primary email and emails.");
			} else if (contactAttributeTypes.includes("primaryAddress") && contactAttributeTypes.includes("addresses")) {
				errors.contactAttributes = t("The contact view cannot contain both primary address and addresses.");
			} else if (contactAttributeTypes.includes("primaryPhone") && contactAttributeTypes.includes("phones")) {
				errors.contactAttributes = t("The contact view cannot contain both primary phone number and phone numbers.");
			}
		}
		return errors;
	};
};

const ContactViewFormDrawerContent: FC<FormDrawerContentProps> = (props) => {
	const { t } = useTranslation();

	return (
		<FormDrawerContent {...props}>
			<TextField name="name" label={t("Name")} required={true} />
			<EditSortableContactAttributesField name="contactAttributes" />
		</FormDrawerContent>
	);
};

export const ContactViewCreateDrawer: FC<CreateDrawerFormProps<ContactViewFormValues>> = (props) => {
	const { t } = useTranslation();
	const validate = useValidate();
	const initialValues: ContactViewFormValues = {
		name: "",
		contactAttributes: [],
		isDefaultContactView: false,
	};

	return (
		<FormDrawer
			{...props}
			initialValues={initialValues}
			title={t("Add contact view")}
			validate={validate}
			renderForm={(formProps) => <ContactViewFormDrawerContent {...formProps} {...props} />}
		/>
	);
};

const mapToFormValues = (
	options: ContactAttributeOption[],
	{ contactAttributes, ...contactView }: ContactViewUpdateResource,
): ContactViewFormValues => ({
	...contactView,
	contactAttributes: contactAttributes.map((attribute) =>
		options.find((option) => option.id === attribute.id),
	) as ContactAttributeOption[],
});

export const ContactViewUpdateDrawer: FC<UpdateDrawerFormProps<ContactViewFormValues>> = (props) => {
	const { t } = useTranslation();
	const validate = useValidate();
	const { contactAttributeOptions, isLoading } = useContactAttributeOptions();

	if (!contactAttributeOptions.length || isLoading) {
		return null;
	}
	return (
		<FormFetchDrawer
			{...props}
			title={t("Edit contact view")}
			queryFn={() =>
				contactViewService.show(props.id).then((x) => {
					return mapToFormValues(contactAttributeOptions, x.data.data);
				})
			}
			queryKey={contactViewService.showKey(props.id)}
			validate={validate}
			renderSuccess={(formProps) => <ContactViewFormDrawerContent {...props} {...formProps} />}
		/>
	);
};
